import {
  type ClientOrchestrationShellStreamItem,
  type FeatureFlagPatch,
  ORCHESTRATION_WS_METHODS,
  WS_METHODS,
} from "@t3tools/contracts";
import type * as Effect from "effect/Effect";
import type * as Stream from "effect/Stream";

import { type WsRpcProtocolClient } from "./wsRpcProtocol.ts";
import { WsTransport } from "./wsTransport.ts";

type RpcTag = keyof WsRpcProtocolClient & string;
type RpcMethod<TTag extends RpcTag> = WsRpcProtocolClient[TTag];
type RpcInput<TTag extends RpcTag> = Parameters<RpcMethod<TTag>>[0];

interface ShellSubscriptionOptions extends StreamSubscriptionOptions {
  readonly afterSequence?: () => number;
}

interface StreamSubscriptionOptions {
  readonly onResubscribe?: () => void;
}

function subscriptionOptions(
  options: StreamSubscriptionOptions | undefined,
  tag: string,
): StreamSubscriptionOptions & { readonly tag: string } {
  return {
    ...options,
    tag,
  };
}

type RpcUnaryMethod<TTag extends RpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Effect.Effect<infer TSuccess, any, any>
    ? (input: RpcInput<TTag>) => Promise<TSuccess>
    : never;

type RpcUnaryNoArgMethod<TTag extends RpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Effect.Effect<infer TSuccess, any, any>
    ? () => Promise<TSuccess>
    : never;

type RpcStreamMethod<TTag extends RpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Stream.Stream<infer TEvent, any, any>
    ? (listener: (event: TEvent) => void, options?: StreamSubscriptionOptions) => () => void
    : never;

type RpcInputStreamMethod<TTag extends RpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Stream.Stream<infer TEvent, any, any>
    ? (
        input: RpcInput<TTag>,
        listener: (event: TEvent) => void,
        options?: StreamSubscriptionOptions,
      ) => () => void
    : never;

export interface WsRpcClient {
  readonly dispose: () => Promise<void>;
  readonly reconnect: () => Promise<void>;
  readonly isHeartbeatFresh: () => boolean;
  readonly projects: {
    readonly searchEntries: RpcUnaryMethod<typeof WS_METHODS.projectsSearchEntries>;
    readonly documentsStatus: RpcUnaryMethod<typeof WS_METHODS.projectsDocumentsStatus>;
    readonly usageReport: RpcUnaryMethod<typeof WS_METHODS.projectsUsageReport>;
    readonly deleteDocument: RpcUnaryMethod<typeof WS_METHODS.projectsDeleteDocument>;
    readonly outputStatus: RpcUnaryMethod<typeof WS_METHODS.projectsOutputStatus>;
    readonly copyOutputToAttachments: RpcUnaryMethod<
      typeof WS_METHODS.projectsCopyOutputToAttachments
    >;
    readonly deleteOutputFile: RpcUnaryMethod<typeof WS_METHODS.projectsDeleteOutputFile>;
  };
  readonly server: {
    readonly getConfig: RpcUnaryNoArgMethod<typeof WS_METHODS.serverGetConfig>;
    readonly upsertKeybinding: RpcUnaryMethod<typeof WS_METHODS.serverUpsertKeybinding>;
    readonly removeKeybinding: RpcUnaryMethod<typeof WS_METHODS.serverRemoveKeybinding>;
    readonly getFeatureFlagsAdmin: RpcUnaryNoArgMethod<
      typeof WS_METHODS.serverGetFeatureFlagsAdmin
    >;
    readonly updateFeatureFlags: (
      patch: FeatureFlagPatch,
    ) => ReturnType<RpcUnaryMethod<typeof WS_METHODS.serverUpdateFeatureFlags>>;
    readonly subscribeConfig: RpcStreamMethod<typeof WS_METHODS.subscribeServerConfig>;
    readonly subscribeLifecycle: RpcStreamMethod<typeof WS_METHODS.subscribeServerLifecycle>;
    readonly subscribeAuthAccess: RpcStreamMethod<typeof WS_METHODS.subscribeAuthAccess>;
    readonly getTraceDiagnostics: RpcUnaryNoArgMethod<typeof WS_METHODS.serverGetTraceDiagnostics>;
    readonly getProcessDiagnostics: RpcUnaryNoArgMethod<
      typeof WS_METHODS.serverGetProcessDiagnostics
    >;
    readonly getProcessResourceHistory: RpcUnaryMethod<
      typeof WS_METHODS.serverGetProcessResourceHistory
    >;
    readonly signalProcess: RpcUnaryMethod<typeof WS_METHODS.serverSignalProcess>;
  };
  readonly orchestration: {
    readonly dispatchCommand: RpcUnaryMethod<typeof ORCHESTRATION_WS_METHODS.dispatchCommand>;
    readonly getArchivedShellSnapshot: RpcUnaryNoArgMethod<
      typeof ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot
    >;
    readonly subscribeShell: (
      listener: (event: ClientOrchestrationShellStreamItem) => void,
      options?: ShellSubscriptionOptions,
    ) => () => void;
    readonly subscribeThread: RpcInputStreamMethod<typeof ORCHESTRATION_WS_METHODS.subscribeThread>;
    readonly fetchOlderThreadMessages: RpcUnaryMethod<
      typeof ORCHESTRATION_WS_METHODS.fetchOlderThreadMessages
    >;
    readonly fetchOlderThreadActivities: RpcUnaryMethod<
      typeof ORCHESTRATION_WS_METHODS.fetchOlderThreadActivities
    >;
    readonly getThreadActivityPayload: RpcUnaryMethod<
      typeof ORCHESTRATION_WS_METHODS.getThreadActivityPayload
    >;
    readonly getTurnInspection: RpcUnaryMethod<typeof ORCHESTRATION_WS_METHODS.getTurnInspection>;
  };
}

export interface CreateWsRpcClientOptions {
  /** Runs immediately before `transport.reconnect()` (e.g. reset reconnect UI/backoff state). */
  readonly beforeReconnect?: () => void;
}

export function createWsRpcClient(
  transport: WsTransport,
  options?: CreateWsRpcClientOptions,
): WsRpcClient {
  return {
    dispose: () => transport.dispose(),
    isHeartbeatFresh: () => transport.isHeartbeatFresh(),
    reconnect: async () => {
      options?.beforeReconnect?.();
      await transport.reconnect();
    },
    projects: {
      searchEntries: (input) =>
        transport.request((client) => client[WS_METHODS.projectsSearchEntries](input)),
      documentsStatus: (input) =>
        transport.request((client) => client[WS_METHODS.projectsDocumentsStatus](input)),
      usageReport: (input) =>
        transport.request((client) => client[WS_METHODS.projectsUsageReport](input)),
      deleteDocument: (input) =>
        transport.request((client) => client[WS_METHODS.projectsDeleteDocument](input)),
      outputStatus: (input) =>
        transport.request((client) => client[WS_METHODS.projectsOutputStatus](input)),
      copyOutputToAttachments: (input) =>
        transport.request((client) => client[WS_METHODS.projectsCopyOutputToAttachments](input)),
      deleteOutputFile: (input) =>
        transport.request((client) => client[WS_METHODS.projectsDeleteOutputFile](input)),
    },
    server: {
      getConfig: () => transport.request((client) => client[WS_METHODS.serverGetConfig]({})),
      upsertKeybinding: (input) =>
        transport.request((client) => client[WS_METHODS.serverUpsertKeybinding](input)),
      removeKeybinding: (input) =>
        transport.request((client) => client[WS_METHODS.serverRemoveKeybinding](input)),
      getFeatureFlagsAdmin: () =>
        transport.request((client) => client[WS_METHODS.serverGetFeatureFlagsAdmin]({})),
      updateFeatureFlags: (patch) =>
        transport.request((client) => client[WS_METHODS.serverUpdateFeatureFlags]({ patch })),
      subscribeConfig: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeServerConfig]({}),
          listener,
          subscriptionOptions(options, WS_METHODS.subscribeServerConfig),
        ),
      subscribeLifecycle: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeServerLifecycle]({}),
          listener,
          subscriptionOptions(options, WS_METHODS.subscribeServerLifecycle),
        ),
      subscribeAuthAccess: (listener, options) =>
        transport.subscribe(
          (client) => client[WS_METHODS.subscribeAuthAccess]({}),
          listener,
          subscriptionOptions(options, WS_METHODS.subscribeAuthAccess),
        ),
      getTraceDiagnostics: () =>
        transport.request((client) => client[WS_METHODS.serverGetTraceDiagnostics]({})),
      getProcessDiagnostics: () =>
        transport.request((client) => client[WS_METHODS.serverGetProcessDiagnostics]({})),
      getProcessResourceHistory: (input) =>
        transport.request((client) => client[WS_METHODS.serverGetProcessResourceHistory](input)),
      signalProcess: (input) =>
        transport.request((client) => client[WS_METHODS.serverSignalProcess](input)),
    },
    orchestration: {
      dispatchCommand: (input) =>
        transport.request((client) => client[ORCHESTRATION_WS_METHODS.dispatchCommand](input)),
      getArchivedShellSnapshot: () =>
        transport.request((client) =>
          client[ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot]({}),
        ),
      subscribeShell: (listener, options) =>
        transport.subscribe(
          (client) =>
            client[ORCHESTRATION_WS_METHODS.subscribeShell](
              options?.afterSequence ? { afterSequence: options.afterSequence() } : {},
            ),
          listener,
          subscriptionOptions(options, ORCHESTRATION_WS_METHODS.subscribeShell),
        ),
      subscribeThread: (input, listener, options) =>
        transport.subscribe(
          (client) => client[ORCHESTRATION_WS_METHODS.subscribeThread](input),
          listener,
          subscriptionOptions(options, ORCHESTRATION_WS_METHODS.subscribeThread),
        ),
      fetchOlderThreadMessages: (input) =>
        transport.request((client) =>
          client[ORCHESTRATION_WS_METHODS.fetchOlderThreadMessages](input),
        ),
      fetchOlderThreadActivities: (input) =>
        transport.request((client) =>
          client[ORCHESTRATION_WS_METHODS.fetchOlderThreadActivities](input),
        ),
      getThreadActivityPayload: (input) =>
        transport.request((client) =>
          client[ORCHESTRATION_WS_METHODS.getThreadActivityPayload](input),
        ),
      getTurnInspection: (input) =>
        transport.request((client) => client[ORCHESTRATION_WS_METHODS.getTurnInspection](input)),
    },
  };
}
